import type { RxTaskListType, RxTask, NotificationRow, Patient, RxTaskType } from '../../api';
import { stripHtmlTags } from '../../utils';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useId } from 'react';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { TabList } from 'design-system/src/components/TabList/TabList';
import { Checkbox } from 'design-system/src/components/Checkbox/Checkbox';
import styled, { css } from 'styled-components';
import { focusVisibleStyles, standardTransitionStyles } from 'design-system/src/sharedStyles';
import { Link } from 'react-router-dom';
import { FlexRow } from 'design-system/src/components/Layout';
import Button from 'design-system/src/components/Button/Button';
import { createTrackingProps } from 'shared-utils/analytics';
import moment from 'moment';
import ChevronRight from 'design-system/src/assets/icons/chevronRight.svg?react';
import ChevronDown from 'design-system/src/assets/icons/chevronDown.svg?react';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import { Caret } from 'design-system/src/components/Caret';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid var(--grayscale-100);
  border-radius: var(--border-radius-medium);
  background: var(--white);
  width: 100%;

  @container content-container (width < 400px) {
    max-width: 400px;
  }
`;

const itemCardStyles = css`
  display: flex;
  padding: 12px 16px;
  text-decoration: none;
  cursor: pointer;
  color: var(--grayscale-900);
  ${standardTransitionStyles}
  border-bottom: 1px solid var(--grayscale-50);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--grayscale-50);
  }

  ${focusVisibleStyles}
`;

const TaskCardContainer = styled(Link)`
  ${itemCardStyles}
`;

const NotificationCardContainer = styled.div`
  ${itemCardStyles}
`;

const ItemDate = styled(Typography)`
  color: var(--grayscale-600);
  white-space: nowrap;

  @container content-container (width < 600px) {
    display: none;
  }
`;

const SmallScreenDate = styled(Typography)`
  color: var(--grayscale-600);
  white-space: nowrap;

  @container content-container (width >= 600px) {
    display: none;
  }
`;

const ItemContent = styled.div<{ $isExpanded?: boolean }>`
  width: 100%;
  max-height: ${({ $isExpanded }) => ($isExpanded ? 'none' : '20px')};
  overflow: hidden;
  ${standardTransitionStyles}

  ${({ $isExpanded }) =>
    !$isExpanded &&
    `
    > * {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `}
`;

const ItemRow = styled.div`
  display: flex;
  width: 100%;
  gap: 24px;
  min-width: 0;

  @container content-container (width < 600px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ItemTopRow = styled.div`
  display: none;
  width: 100%;
  gap: 8px;
  align-items: flex-start;

  @container content-container (width < 600px) {
    display: flex;
  }
`;

const ItemDateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: space-between;
`;

const CaretContainer = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemCenterColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const ItemRightColumn = styled.div`
  flex: 0 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  min-width: 20px;
  max-width: 20px;

  @container content-container (width < 600px) {
    display: none;
  }
`;

const PreviewText = styled(Typography)`
  color: var(--grayscale-600);
`;

const TruncatedPreviewText = styled(PreviewText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LineBreak = styled.div`
  height: 8px;
`;

const TitleRow = styled(FlexRow)`
  // Using && to increase CSS specificity when extending a styled component.
  // This ensures these style overrides take precedence over the base FlexRow styles
  // without needing !important, which is more maintainable.
  && {
    margin-bottom: 16px;
    height: 96px;
    align-items: flex-start;
    flex-direction: column;
  }

  @container content-container (width >= 600px) {
    && {
      height: 48px;
      flex-direction: row;
      align-items: center;
      gap: 0;
    }
  }
`;

const TabListContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const RightContentContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;

  @container content-container (width >= 600px) {
    width: auto;
  }
`;

const CheckboxContainer = styled(FlexRow)`
  align-items: center;
  gap: 4px;
`;

const CreateTaskButton = styled(Button)`
  margin-left: 16px;
`;

const ActionItemsContainer = styled.div`
  margin-bottom: 32px;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  border: 2px dotted var(--grayscale-100);
  border-radius: var(--border-radius-medium);
  text-align: center;
`;

const EmptyStateContent = styled.div`
  max-width: 400px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const formatPreviewText = (text: string, isExpanded: boolean = false) => {
  // Remove any formatting from the text
  const noFormattingText = stripHtmlTags(text);
  if (!isExpanded) {
    // Remove any newlines from the text
    const oneLineText = noFormattingText.replace(/\\n/g, ' ');
    return <TruncatedPreviewText styledAs="bodyExtraSmallDMSans">{oneLineText}</TruncatedPreviewText>;
  }

  return noFormattingText.split('\n').map((line, index) => {
    if (line === '') {
      return <LineBreak key={index} />;
    }
    return (
      <PreviewText styledAs="bodyExtraSmallDMSans" key={index}>
        <RenderHtmlString>{line}</RenderHtmlString>
      </PreviewText>
    );
  });
};

const getTaskUrl = (taskType: RxTaskType, id: string) => {
  const table = taskType === 'prior_auth_review' ? 'prior-auth-reviews' : 'tasks';
  return `/${table}/${id}`;
};

const getTabTitle = (task_list_type: RxTaskListType) => {
  if (task_list_type === 'all') {
    return 'Tasks and reviews';
  }
  if (task_list_type === 'tasks') {
    return 'Tasks';
  }
  if (task_list_type === 'reviews') {
    return 'Reviews';
  }
  return 'Tasks';
};

function getTaskEmptyStateTitle(task_list_type: RxTaskListType, showAllTasks: boolean): string {
  if (task_list_type === 'all') {
    return `No ${showAllTasks ? 'open' : ''} tasks or reviews found`;
  }
  if (task_list_type === 'reviews') {
    return 'No reviews found';
  }
  return `No ${showAllTasks ? 'open' : ''} tasks found`;
}

function getTaskEmptyStateLines(task_list_type: RxTaskListType, showAllTasks: boolean): string[] {
  const itemsText = task_list_type === 'all' ? 'tasks or reviews' : task_list_type === 'reviews' ? 'reviews' : 'tasks';
  if (!showAllTasks && task_list_type === 'tasks') {
    return [
      `There are no open ${itemsText} associated with this prescription.`,
      'Click the checkbox above to show completed tasks and check back for future updates.',
    ];
  }
  return [`There are no ${itemsText} associated with this prescription.`, 'Please check back for future updates.'];
}

function TaskEmptyState({ task_list_type, showAllTasks }: { task_list_type: RxTaskListType; showAllTasks: boolean }) {
  return (
    <EmptyStateContainer {...createTrackingProps({ unmask: true })}>
      <EmptyStateContent>
        <Typography styledAs="bodySmallDMSans" weight={600} color="var(--grayscale-600)">
          {getTaskEmptyStateTitle(task_list_type, showAllTasks)}
        </Typography>
        {getTaskEmptyStateLines(task_list_type, showAllTasks).map((line, index) => (
          <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)" key={index}>
            {line}
          </Typography>
        ))}
      </EmptyStateContent>
    </EmptyStateContainer>
  );
}

function TaskCard({ taskUrl, task, taskId }: { taskUrl: string; task: RxTask; taskId: string }) {
  const navigate = useNavigate();
  return (
    <TaskCardContainer to={taskUrl} key={taskId}>
      <ItemRow>
        <ItemDate styledAs="bodyExtraSmallDMSans" {...createTrackingProps({ unmask: true })}>
          {moment(task.created_at).format('MM/DD/YY')}
        </ItemDate>
        <ItemTopRow {...createTrackingProps({ unmask: true })}>
          <ItemDateContainer>
            <SmallScreenDate styledAs="bodyExtraSmallDMSans">
              {moment(task.created_at).format('MM/DD/YY')}
            </SmallScreenDate>
            <CaretContainer>
              <Caret
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  navigate(taskUrl);
                }}
              >
                <ChevronRight aria-hidden={true} />
              </Caret>
            </CaretContainer>
          </ItemDateContainer>
        </ItemTopRow>
        <ItemCenterColumn>
          <Typography styledAs="bodySmallDMSans" weight={500} {...createTrackingProps({ unmask: true })}>
            {task.sub_type}
          </Typography>
          {/* DO NOT unmask task preview - could contain PHI */}
          {task.extra_context && (
            <ItemContent $isExpanded={false}>{formatPreviewText(task.extra_context, false)}</ItemContent>
          )}
        </ItemCenterColumn>
        <ItemRightColumn>
          <Caret
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              navigate(taskUrl);
            }}
          >
            <ChevronRight aria-hidden={true} />
          </Caret>
        </ItemRightColumn>
      </ItemRow>
    </TaskCardContainer>
  );
}

function NotificationEmptyState() {
  return (
    <EmptyStateContainer {...createTrackingProps({ unmask: true })}>
      <EmptyStateContent>
        <Typography styledAs="bodySmallDMSans" weight={600} color="var(--grayscale-600)">
          No notifications found
        </Typography>
        <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
          There are currently no notifications associated with this prescription. Please check back for future updates.
        </Typography>
      </EmptyStateContent>
    </EmptyStateContainer>
  );
}

function NotificationCard({
  notification,
  isExpanded,
  setExpandedNotificationId,
}: {
  notification: NotificationRow;
  isExpanded: boolean;
  setExpandedNotificationId: (id: string | null) => void;
}) {
  const notif = notification;
  return (
    <NotificationCardContainer
      key={`notif-${notif.id}`}
      onClick={() => {
        if (notif.extra_context) {
          setExpandedNotificationId(isExpanded ? null : notif.id);
        }
      }}
    >
      <ItemRow>
        <ItemDate styledAs="bodyExtraSmallDMSans" {...createTrackingProps({ unmask: true })}>
          {moment(notif.created_at).format('MM/DD/YY')}
        </ItemDate>
        <ItemTopRow {...createTrackingProps({ unmask: true })}>
          <ItemDateContainer>
            <SmallScreenDate styledAs="bodyExtraSmallDMSans">
              {moment(notif.created_at).format('MM/DD/YY')}
            </SmallScreenDate>
            {notif.extra_context && (
              <CaretContainer>
                <Caret
                  isOpen={isExpanded}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setExpandedNotificationId(isExpanded ? null : notif.id);
                  }}
                >
                  <ChevronDown aria-hidden={true} />
                </Caret>
              </CaretContainer>
            )}
          </ItemDateContainer>
        </ItemTopRow>
        <ItemCenterColumn>
          <Typography styledAs="bodySmallDMSans" weight={500} {...createTrackingProps({ unmask: true })}>
            {notif.task_type}
          </Typography>
          {/* DO NOT unmask preview - could contain PHI */}
          {notif.extra_context && (
            <ItemContent $isExpanded={isExpanded}>{formatPreviewText(notif.extra_context, isExpanded)}</ItemContent>
          )}
        </ItemCenterColumn>
        {notif.extra_context && (
          <ItemRightColumn>
            <Caret
              isOpen={isExpanded}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setExpandedNotificationId(isExpanded ? null : notif.id);
              }}
            >
              <ChevronDown aria-hidden={true} />
            </Caret>
          </ItemRightColumn>
        )}
      </ItemRow>
    </NotificationCardContainer>
  );
}

export const ActionItemsAndUpdatesSection = ({
  task_list_type,
  taskList,
  notificationsList,
  patient,
}: {
  task_list_type: RxTaskListType;
  taskList: RxTask[];
  notificationsList: NotificationRow[];
  patient: Patient;
}) => {
  const [activeTab, setActiveTab] = useState<'tasks' | 'notifications'>('tasks');
  const [expandedNotificationId, setExpandedNotificationId] = useState<string | null>(null);
  const navigate = useNavigate();
  const mainTabListId = useId();
  const mainTabPanelId = useId();
  const [showAllTasks, setShowAllTasks] = useState(false);
  const filteredTaskList = showAllTasks ? taskList : taskList.filter((task) => task.status === 'open');

  return (
    <ActionItemsContainer>
      <Typography
        styledAs="bodyLargeSpaceGrotesk"
        renderedAs="h2"
        weight={500}
        {...createTrackingProps({ unmask: true })}
      >
        Action items and updates
      </Typography>
      <TitleRow {...createTrackingProps({ unmask: true })}>
        <TabListContainer>
          <TabList
            tabs={[
              {
                value: 'tasks',
                label: getTabTitle(task_list_type),
              },
              {
                value: 'notifications',
                label: 'Notifications',
              },
            ]}
            selectedValue={activeTab}
            onSelect={(value) => setActiveTab(value as 'tasks' | 'notifications')}
            tabListId={mainTabListId}
            tabPanelId={mainTabPanelId}
            ariaLabel="Tasks and notifications tabs"
            {...createTrackingProps({ elementName: 'Prescription Page: Action Tabs' })}
          />
        </TabListContainer>
        <RightContentContainer>
          {activeTab === 'tasks' && (
            <>
              <CheckboxContainer>
                <Checkbox
                  checked={showAllTasks}
                  onChange={(e) => setShowAllTasks(e.target.checked)}
                  inputProps={{ 'aria-label': 'Show completed tasks' }}
                />
                <Typography styledAs="bodySmallDMSans">Show completed tasks</Typography>
              </CheckboxContainer>
              <CreateTaskButton
                {...createTrackingProps({ elementName: 'Prescription Page: Create Task Button' })}
                variant="tertiary"
                size="sm"
                onClick={() => {
                  navigate(`new-task?patientId=${patient.id}&source=portal_rx_page`);
                }}
              >
                Create task
              </CreateTaskButton>
            </>
          )}
        </RightContentContainer>
      </TitleRow>
      <div role="tabpanel" id={mainTabPanelId} aria-labelledby={mainTabListId}>
        {activeTab === 'tasks' ? (
          <>
            {!filteredTaskList.length ? (
              <TaskEmptyState task_list_type={task_list_type} showAllTasks={showAllTasks} />
            ) : (
              <Container>
                {filteredTaskList.map((task: RxTask) => {
                  const taskUrl = getTaskUrl(task.type, task.id);
                  return <TaskCard key={task.id} taskUrl={taskUrl} task={task} taskId={task.id} />;
                })}
              </Container>
            )}
          </>
        ) : (
          <>
            {notificationsList.length === 0 ? (
              <NotificationEmptyState />
            ) : (
              <Container>
                {notificationsList.map((notif: NotificationRow) => {
                  const isExpanded = expandedNotificationId === notif.id;
                  return (
                    <NotificationCard
                      key={notif.id}
                      notification={notif}
                      isExpanded={isExpanded}
                      setExpandedNotificationId={setExpandedNotificationId}
                    />
                  );
                })}
              </Container>
            )}
          </>
        )}
      </div>
    </ActionItemsContainer>
  );
};
